import axios from "axios";

// SONA shared backend. Its CORS whitelists the production hosts (alwaysfreshmusic.com, www
// and the API host) and the owner confirmed credentialed CORS works from there, so in PRODUCTION
// we call SONA DIRECTLY (no proxy). Only the dev PREVIEW origin is NOT whitelisted by SONA, so
// there we keep going through our own FastAPI proxy (/api/afm), which forwards server-side.
const SONA_ORIGIN = process.env.REACT_APP_AFM_API || "https://creative-coliving.emergent.host";
const IS_PREVIEW = typeof window !== "undefined" && window.location.hostname.includes("preview.emergentagent.com");
const AFM_API = IS_PREVIEW ? `${process.env.REACT_APP_BACKEND_URL}/api/afm` : `${SONA_ORIGIN}/api`;
// Local endpoints served by our own FastAPI backend (cover processing, access requests, etc.).
const LOCAL_API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const localApi = axios.create({ baseURL: LOCAL_API });
export const api = axios.create({ baseURL: AFM_API });

// SEGURIDAD: adjunta la sesión real (token SONA) en TODAS las llamadas, tanto a
// SONA como a nuestro backend. Antes la identidad viajaba solo como `viewer_id`
// en texto, y el servidor se la creía. Ahora el servidor valida contra el token.
function _attachToken(config) {
  try {
    const t = typeof window !== "undefined" && window.localStorage.getItem("sona_session_token");
    if (t) {
      config.headers = config.headers || {};
      if (!config.headers.Authorization) config.headers.Authorization = `Bearer ${t}`;
    }
  } catch (e) { /* localStorage no disponible */ }
  return config;
}
localApi.interceptors.request.use(_attachToken);
api.interceptors.request.use(_attachToken);

// 2FA: si la sesión todavía no puso el código del correo, el servidor responde 403 "mfa_required".
// Llevamos a la persona a la pantalla del código (una sola vez, sin bucles).
function _mfaRedirect(err) {
  try {
    if (err?.response?.status === 403 && err?.response?.data?.detail === "mfa_required"
        && typeof window !== "undefined" && !window.location.pathname.startsWith("/verificar")) {
      window.location.assign("/verificar");
    }
  } catch (e) { /* noop */ }
  return Promise.reject(err);
}
// Sesión vencida o cerrada: si una llamada responde 401, confirmamos con /auth/me. Si la sesión de
// verdad ya no vale, limpiamos el navegador y mandamos a iniciar sesión (en vez de dejar pantallas
// rotas con "No autorizado" o "No pudimos cargar los datos").
let _checkingSession = false;
async function _sessionGuard(err) {
  try {
    const url = String(err?.config?.url || "");
    if (err?.response?.status === 401 && !_checkingSession && !url.includes("/auth/") && typeof window !== "undefined") {
      const t = window.localStorage.getItem("sona_session_token");
      const p = window.location.pathname;
      if (t && !p.startsWith("/login") && !p.startsWith("/verificar")) {
        _checkingSession = true;
        const alive = await api.get("/auth/me").then(() => true).catch((e) => e?.response?.status !== 401);
        _checkingSession = false;
        if (!alive) {
          window.localStorage.removeItem("sona_session_token");
          window.localStorage.removeItem("afm_user");
          window.location.assign("/login?vencida=1");
        }
      }
    }
  } catch (e) { _checkingSession = false; }
  return Promise.reject(err);
}
api.interceptors.response.use((r) => r, (e) => _mfaRedirect(e).catch(_sessionGuard));
localApi.interceptors.response.use((r) => r, (e) => _mfaRedirect(e).catch(_sessionGuard));

// Identificador de este navegador (para "recordar este dispositivo" en el 2FA). No es secreto.
export const deviceInfo = () => {
  let id = "";
  try {
    id = window.localStorage.getItem("afm_device_id") || "";
    if (!id) {
      id = (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID()
        : `web-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
      window.localStorage.setItem("afm_device_id", id);
    }
  } catch (e) { /* sin localStorage: el código se pedirá cada vez */ }
  return { device_id: id || undefined, device_label: "Navegador · Always Fresh", device_platform: "web" };
};

// ---- Cover processing ----
export const processCover = (file, mode, crop) => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("mode", mode);
  if (crop) {
    fd.append("crop_x", Math.round(crop.x));
    fd.append("crop_y", Math.round(crop.y));
    fd.append("crop_w", Math.round(crop.width));
    fd.append("crop_h", Math.round(crop.height));
  }
  return localApi.post("/cover/process", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};

// ---- Asset upload (audio master etc.) to shared backend ----
export const uploadAsset = (userId, file, onProgress) => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("user_id", userId);
  return localApi.post("/uploads/asset", fd, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (e) => { if (onProgress && e.total) onProgress(Math.min(99, Math.round((e.loaded / e.total) * 100))); },
  }).then((r) => r.data);
};

// ---- Withdrawals (shared SONA backend) ----
export const createWithdrawal = (payload) =>
  api.post(`/distribution/withdrawals`, payload).then((r) => r.data);

export const getWithdrawals = (userId) =>
  api.get(`/distribution/withdrawals/${userId}`).then((r) => r.data);

// ---- Cover design requests (local backend; manual payment) ----
export const createCoverRequest = (payload) =>
  localApi.post("/cover-requests", payload).then((r) => r.data);
export const getUserCoverRequests = (userId) =>
  localApi.get(`/cover-requests/user/${userId}`).then((r) => r.data);
export const getAdminCoverRequests = (viewerId, status = "") =>
  localApi.get(`/cover-requests`, { params: { viewer_id: viewerId, status } }).then((r) => r.data);
export const decideCoverRequest = (id, payload) =>
  localApi.post(`/cover-requests/${id}/status`, payload).then((r) => r.data);
export const notifyNewRelease = (payload) =>
  localApi.post("/notify/new-release", payload).then((r) => r.data).catch(() => {});

export const sendReleaseReceipt = (payload) =>
  localApi.post("/release-receipt", payload).then((r) => r.data).catch(() => {});

export const notifyReleaseStatus = (payload) =>
  localApi.post("/notify/release-status", payload).then((r) => r.data).catch(() => {});

// ---- SONA AI Music Manager + marketing ----
// ---- Mánager SONA (uno solo para SONA y Always Fresh) ----
// Vive en el backend de SONA: misma memoria, mismo saldo de gestiones y la clave propia de IA
// de SONA (Gemini).
export const aiManagerChat = ({ user_id, message }) =>
  api.post("/manager/chat", { user_id, message }).then((r) => ({ reply: r.data?.reply?.text || "" }));
// "Generar estrategia" = gestión "Textos para redes" del Mánager SONA (usa 1 gestión del mes).
export const aiContentStrategy = ({ user_id, title }) =>
  api.post("/manager/task", {
    user_id, task: "textos",
    brief: `Necesito 5 ideas de contenido para TikTok y Reels para promocionar mi tema "${title}". Cada idea en una línea que empiece con "-", con el hook y la acción, lista para grabar.`,
  }).then((r) => {
    const text = r.data?.reply?.text || "";
    const lines = text.split("\n").map((l) => l.trim()).filter((l) => /^([-•*]|\d+[.)])\s+/.test(l))
      .map((l) => l.replace(/^([-•*]|\d+[.)])\s+/, "").replace(/\*\*/g, "").trim()).filter(Boolean);
    return { ideas: lines.length ? lines.slice(0, 8) : [text], capacity: r.data?.capacity };
  });
export const getManagerCapacity = (userId) =>
  api.get(`/manager/capacity/${userId}`).then((r) => r.data).catch(() => null);
export const createMarketingRequest = (payload) => localApi.post("/marketing-requests", payload).then((r) => r.data);
export const getMarketingEligibility = (userId) => localApi.get(`/marketing-requests/eligibility`, { params: { user_id: userId } }).then((r) => r.data);
export const getAdminMarketingRequests = (viewerId, status = "") => localApi.get(`/marketing-requests`, { params: { viewer_id: viewerId, status } }).then((r) => r.data);
export const decideMarketingRequest = (id, payload) => localApi.post(`/marketing-requests/${id}/status`, payload).then((r) => r.data);
export const getAiHistory = (userId) =>
  api.get(`/manager/chat/${userId}`)
    .then((r) => (r.data?.messages || []).map((m) => ({ role: m.role, content: m.text || "" })))
    .catch(() => []);
export const searchArtists = (q, limit = 6) => localApi.get("/artists/search", { params: { q, limit } }).then((r) => r.data);
export const getFreshFinds = () => localApi.get("/fresh-finds").then((r) => r.data);
export const getFreshFindArtist = (name) => localApi.get("/fresh-finds/artist", { params: { name } }).then((r) => r.data);
export const getFreshFindsAdmin = (viewerId) => localApi.get("/fresh-finds/admin", { params: { viewer_id: viewerId } }).then((r) => r.data);
export const syncFreshFinds = (viewerId) => localApi.post("/fresh-finds/sync", null, { params: { viewer_id: viewerId } }).then((r) => r.data);
export const patchFreshFind = (id, payload) => localApi.patch(`/fresh-finds/${id}`, payload).then((r) => r.data);
export const getSpotifyStatus = (viewerId) => localApi.get("/spotify/status", { params: { viewer_id: viewerId } }).then((r) => r.data);
export const getSpotifyAuthUrl = (viewerId) => localApi.get("/spotify/auth-url", { params: { viewer_id: viewerId } }).then((r) => r.data);
export const disconnectSpotify = (viewerId) => localApi.post("/spotify/disconnect", null, { params: { viewer_id: viewerId } }).then((r) => r.data);
export const saveArtist = (payload) => localApi.post("/artists", payload).then((r) => r.data);
export const getMyArtist = (email) => localApi.get("/artists/me", { params: { email } }).then((r) => r.data).catch(() => null);
export const updateMyArtistBio = (payload) => localApi.patch("/artists/me", payload).then((r) => r.data);
export const freshFindShareUrl = (id, format = "square") => `${process.env.REACT_APP_BACKEND_URL}/api/fresh-finds/${id}/share.png${format === "story" ? "?format=story" : ""}`;
export const notifyFreshFind = (id, viewerId) => localApi.post(`/fresh-finds/${id}/notify`, null, { params: { viewer_id: viewerId } }).then((r) => r.data);
export const linkMyArtist = (payload) => localApi.post("/artists/me", payload).then((r) => r.data);
export const getArtistDirectory = (viewerId, q = "") => localApi.get("/artists", { params: { viewer_id: viewerId, q } }).then((r) => r.data);
export const updateArtist = (id, payload) => localApi.put(`/artists/${id}`, payload).then((r) => r.data);
export const mergeArtists = (payload) => localApi.post("/artists/merge", payload).then((r) => r.data);
export const deleteArtist = (id, viewerId) => localApi.delete(`/artists/${id}`, { params: { viewer_id: viewerId } }).then((r) => r.data);
export const notifySplitInvite = (payload) => localApi.post("/notify/split-invite", payload).then((r) => r.data).catch(() => {});

// ---- Support (shared SONA backend; AFM tickets notify info@alwaysfreshmusic.com) ----
export const createTicket = (payload) =>
  api.post("/support/tickets", { ...payload, source: "afm" }).then((r) => r.data);

export const getTickets = (userId) =>
  api.get(`/support/tickets/user/${userId}`).then((r) => r.data);

export const getTicket = (id, viewerId) =>
  api.get(`/support/tickets/${id}`, { params: { viewer_id: viewerId } }).then((r) => r.data);

export const postTicketMessage = (id, payload) =>
  api.post(`/support/tickets/${id}/messages`, payload).then((r) => r.data);

// ---- Auth ----
// Todo usuario de SONA debe poder entrar a AFM. Intentamos con platform:"afm";
// si SONA bloquea por plataforma (403), reintentamos sin restricción.
export const login = async (email, password) => {
  try {
    const r = await api.post("/auth/login", { email, password, platform: "afm", ...deviceInfo(), mfa_capable: true });
    return r.data;
  } catch (e) {
    if (e?.response?.status === 403) {
      const r = await api.post("/auth/login", { email, password, ...deviceInfo(), mfa_capable: true });
      return r.data;
    }
    throw e;
  }
};

export const register = (payload) =>
  api.post("/distribution/register", payload).then((r) => r.data);

// Persist extended onboarding fields locally until the shared backend maps them.
export const saveOnboardingProfile = (payload) =>
  localApi.post("/onboarding/profile", payload).then((r) => r.data).catch(() => {});

// Access requests (pending approval registration)
export const createAccessRequest = (payload) =>
  localApi.post("/access-requests", payload).then((r) => r.data);
export const getAccessRequests = (viewerId, status = "") =>
  localApi.get(`/access-requests`, { params: { viewer_id: viewerId, status } }).then((r) => r.data);
export const decideAccessRequest = (id, payload) =>
  localApi.post(`/access-requests/${id}/decide`, payload).then((r) => r.data);
export const adminCreateAccount = (payload) =>
  localApi.post("/admin/create-account", payload).then((r) => r.data);
export const googleSessionLogin = (session_id) =>
  localApi.post("/auth/google-session", { session_id, platform: "afm" }).then((r) => r.data);
export const appleSessionLogin = (identity_token, name, email) =>
  localApi.post("/auth/apple-session", { identity_token, name, email, platform: "afm", device_id: deviceInfo().device_id }).then((r) => r.data);

// Split sheet external invitees (persisted locally so they show as 'Invitado' in the detail)
export const saveSplitInvitees = (payload) =>
  localApi.post("/split-invitees", payload).then((r) => r.data).catch(() => {});
export const getSplitInvitees = (sessionId) =>
  localApi.get(`/split-invitees/${sessionId}`).then((r) => r.data).catch(() => []);
export const getInviteeByToken = (token) =>
  localApi.get(`/split-invitees/token/${token}`).then((r) => r.data);
export const signInvitee = (token, payload) =>
  localApi.post(`/split-invitees/${token}/sign`, payload).then((r) => r.data);

export const logout = () => api.post("/auth/logout").catch(() => {});

// ---- Verificación en dos pasos (código por correo) ----
export const mfaVerify = (code, remember = true) =>
  api.post("/auth/mfa/verify", { code, remember, ...deviceInfo() }).then((r) => r.data);
export const mfaResend = () => api.post("/auth/mfa/resend").then((r) => r.data);
export const mfaStatus = () => api.get("/auth/mfa/status").then((r) => r.data);

// ---- Password recovery / change (code-based, native SONA) ----
export const forgotPassword = (email) =>
  api.post("/auth/forgot", { email, platform: "afm" }).then((r) => r.data);
export const resetPassword = (email, token, new_password) =>
  api.post("/auth/reset", { email, token, new_password }).then((r) => r.data);

// ---- Pre-save ----
export const createPresave = (viewerId, releaseId) =>
  api.post("/distribution/presave", { viewer_id: viewerId, release_id: releaseId }).then((r) => r.data);

export const getPresaveByRelease = (releaseId) =>
  api.get(`/distribution/presave/release/${releaseId}`).then((r) => r.data);

export const getPresave = (id) =>
  api.get(`/distribution/presave/${id}`).then((r) => r.data);

export const trackPresave = (id) =>
  api.post(`/distribution/presave/${id}/track`).then((r) => r.data);

export const getAdminPresaves = (viewerId) =>
  api.get(`/distribution/admin/presaves`, { params: { viewer_id: viewerId } }).then((r) => r.data);

// ---- Pitching IA ----
export const startEvaluation = (viewerId, releaseId) => api.post(`/pitching/evaluate`, { viewer_id: viewerId, release_id: releaseId }).then((r) => r.data);
export const getEvaluation = (releaseId) => api.get(`/pitching/evaluation/${releaseId}`).then((r) => r.data);

// ---- Smart Links ----
export const generateSmartlink = (viewerId, releaseId) => api.post(`/smartlinks/generate`, { viewer_id: viewerId, release_id: releaseId }).then((r) => r.data);
export const getSmartlinkByRelease = (releaseId) => api.get(`/smartlinks/release/${releaseId}`).then((r) => r.data);
export const getSmartlink = (slug) => api.get(`/smartlinks/${slug}`).then((r) => r.data);
export const clickSmartlink = (slug, dsp) => api.post(`/smartlinks/${slug}/click`, null, { params: { dsp } }).then((r) => r.data);
export const updatePixels = (releaseId, payload) => api.put(`/smartlinks/${releaseId}/pixels`, payload).then((r) => r.data);

// ---- Predictive ----
export const getForecast = (userId) => api.get(`/predictive/forecast/${userId}`).then((r) => r.data);
export const getOpportunities = (userId) => api.get(`/predictive/opportunities/${userId}`).then((r) => r.data);

// ---- Alerts ----
export const getAlerts = (userId, onlyUnread) => api.get(`/alerts/user/${userId}`, { params: onlyUnread ? { only_unread: true } : {} }).then((r) => r.data);
export const markAlertRead = (id) => api.post(`/alerts/${id}/read`).then((r) => r.data);
export const markAllAlertsRead = (userId) => api.post(`/alerts/user/${userId}/read-all`).then((r) => r.data);

// ---- Creative + EPK ----
export const generateCanvas = (viewerId, releaseId) => api.post(`/creative/canvas`, { viewer_id: viewerId, release_id: releaseId }).then((r) => r.data);
export const getCreative = (releaseId) => api.get(`/creative/release/${releaseId}`).then((r) => r.data);
export const getEpk = (slug) => api.get(`/epk/${slug}`).then((r) => r.data);
export const getEpkByUser = (userId) => api.get(`/epk/user/${userId}`).then((r) => r.data);
export const updateEpk = (userId, payload) => api.put(`/epk/${userId}`, payload).then((r) => r.data);
export const uploadEpkImage = (userId, contentType, dataBase64) =>
  localApi.post(`/epk/${userId}/upload`, { viewer_id: userId, content_type: contentType, data_base64: dataBase64 }).then((r) => r.data);

// Resolve a backend asset path (relative `/api/...`). Prod: direct to SONA; preview: via proxy.
export const resolveAsset = (u) => {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  if (u.startsWith("/api/files/")) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
  if (u.startsWith("/api/")) return IS_PREVIEW ? `${process.env.REACT_APP_BACKEND_URL}/api/afm${u.slice(4)}` : `${SONA_ORIGIN}${u}`;
  return u;
};

// ---- Admin back-office (viewer_id must be super_admin / distribution_admin) ----
export const getAdminOverview = (viewerId) =>
  api.get(`/distribution/admin/overview`, { params: { viewer_id: viewerId } }).then((r) => r.data);
export const getAdminArtists = (viewerId) =>
  api.get(`/distribution/admin/artists`, { params: { viewer_id: viewerId } }).then((r) => r.data);
export const getAdminReleases = (viewerId, status = "") =>
  api.get(`/distribution/admin/releases`, { params: { viewer_id: viewerId, status } }).then((r) => r.data);
export const setReleaseStatus = (releaseId, payload) =>
  api.post(`/distribution/admin/release/${releaseId}/status`, payload).then((r) => r.data);

// Phase 2 — withdrawals admin
export const getAdminWithdrawals = (viewerId, status = "") =>
  api.get(`/distribution/admin/withdrawals`, { params: { viewer_id: viewerId, status } }).then((r) => r.data);
export const decideWithdrawal = (id, payload) =>
  api.post(`/distribution/admin/withdrawals/${id}/decide`, payload).then((r) => r.data);
export const createPayout = (payload) =>
  api.post(`/distribution/admin/payout`, payload).then((r) => r.data);
export const getAdminPayouts = (viewerId, userId = "") =>
  api.get(`/distribution/admin/payouts`, { params: { viewer_id: viewerId, user_id: userId } }).then((r) => r.data);

// Phase 3 — support admin / pitching / smartlinks
export const getAllTickets = (viewerId, status = "") =>
  api.get(`/support/tickets`, { params: { viewer_id: viewerId, status } }).then((r) => r.data);
export const patchTicketStatus = (id, payload) =>
  api.patch(`/support/tickets/${id}/status`, payload).then((r) => r.data);
export const getPitchingQueue = (viewerId) =>
  api.get(`/pitching/admin/queue`, { params: { viewer_id: viewerId } }).then((r) => r.data);
export const getAllSmartlinks = (viewerId) =>
  api.get(`/smartlinks/admin/all`, { params: { viewer_id: viewerId } }).then((r) => r.data);

// Phase 4 — staff / access
export const getStaff = (viewerId) =>
  api.get(`/distribution/admin/staff`, { params: { viewer_id: viewerId } }).then((r) => r.data);
export const setAdminAccess = (payload) =>
  api.post(`/distribution/admin/access`, payload).then((r) => r.data);
export const getSettings = (viewerId) =>
  api.get(`/distribution/admin/settings`, { params: { viewer_id: viewerId } }).then((r) => r.data);
export const updateSettings = (payload) =>
  api.post(`/distribution/admin/settings`, payload).then((r) => r.data);

export const AFM_WS_BASE = (IS_PREVIEW ? (process.env.REACT_APP_AFM_API || "") : SONA_ORIGIN).replace(/^http/, "ws");
export const AFM_ORIGIN = SONA_ORIGIN;
export const authMe = (token) =>
  api.get("/auth/me", { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.data);

// ---- Analytics ----
export const getAnalytics = (userId, range = "28d") =>
  api.get(`/distribution/analytics/${userId}`, { params: { range } }).then((r) => r.data);

// ---- Royalties / Wallet ----
export const getRoyalties = (userId) =>
  api.get(`/distribution/royalties/${userId}`).then((r) => r.data);

export const getDistUser = (userId) =>
  api.get(`/distribution/user/${userId}`).then((r) => r.data);

// ---- Releases ----
export const getReleases = (userId) =>
  api.get(`/releases/user/${userId}`).then((r) => r.data);

export const submitRelease = (payload) =>
  api.post(`/releases`, payload).then((r) => r.data);

export const getReleaseDetail = (releaseId) =>
  api.get(`/distribution/release/${releaseId}`).then((r) => r.data);

// ---- Split Sheets / Sessions ----
export const searchUsers = (q) =>
  api.get(`/users/search`, { params: { q } }).then((r) => r.data);

export const createSession = (payload) =>
  api.post(`/sessions`, payload).then((r) => r.data);

export const getSessions = (userId) =>
  api.get(`/sessions/user/${userId}`).then((r) => r.data);

export const getSession = (id, viewerId) =>
  api.get(`/sessions/${id}`, { params: { viewer_id: viewerId } }).then((r) => r.data);

export const signSession = (id, payload) =>
  api.post(`/sessions/${id}/sign`, payload).then((r) => r.data);

export const linkRelease = (id, payload) =>
  api.post(`/sessions/${id}/link-release`, payload).then((r) => r.data);

export const getSessionStats = (userId) =>
  api.get(`/sessions/user/${userId}/stats`).then((r) => r.data);

export const notifyGuestSign = (payload) => localApi.post("/notify/guest-sign", payload).then((r) => r.data);
export const getGuestSession = (token) => api.get(`/guest/${token}`).then((r) => r.data);
export const signGuestSession = (token, payload) => api.post(`/guest/${token}`, payload).then((r) => r.data);
export const DOC_TYPES = { integrated: "Integrado", master: "Contrato Master", publishing: "Contrato Publishing" };
export const sessionPdfUrl = (id, viewerId) => `${LOCAL_API}/sessions/${id}/pdf?viewer_id=${encodeURIComponent(viewerId || "")}`;
export const getPendingSessions = (userId) => api.get(`/sessions/pending/${userId}`).then((r) => r.data);
export const watchSplitSession = (payload) => localApi.post("/split-watch", payload).then((r) => r.data);

// ---- Finance ----
export const getFinance = (userId) =>
  api.get(`/finance/user/${userId}`).then((r) => r.data);

// ---- Export ----
export const exportUrl = (userId, { kind, format, date_from, date_to, release_id, store }) => {
  const params = new URLSearchParams({ kind, format });
  if (date_from) params.set("date_from", date_from);
  if (date_to) params.set("date_to", date_to);
  if (release_id) params.set("release_id", release_id);
  if (store) params.set("store", store);
  return `${AFM_API}/distribution/export/${userId}?${params.toString()}`;
};

// ---- la distribuidora Distro (envío de lanzamientos, solo admin) ----
export const adamantemSubmissions = () =>
  localApi.get(`/adamantem/submissions`).then((r) => r.data);
export const adamantemGet = (releaseId) =>
  localApi.get(`/adamantem/releases/${releaseId}`).then((r) => r.data);
export const adamantemPreview = (releaseId) =>
  localApi.get(`/adamantem/releases/${releaseId}/preview`).then((r) => r.data);
export const adamantemSend = (releaseId) =>
  localApi.post(`/adamantem/releases/${releaseId}/send`).then((r) => r.data);
export const adamantemRefresh = (releaseId) =>
  localApi.post(`/adamantem/releases/${releaseId}/refresh`).then((r) => r.data);
export const saveReleaseData = (payload) =>
  localApi.post(`/adamantem/release-data`, payload).then((r) => r.data);
export const adamantemAdjustGet = (releaseId) =>
  localApi.get(`/adamantem/releases/${releaseId}/adjust`).then((r) => r.data);
export const adamantemAdjustSave = (releaseId, body) =>
  localApi.put(`/adamantem/releases/${releaseId}/adjust`, body).then((r) => r.data);
export const adamantemCopyCreate = (releaseId, body) =>
  localApi.post(`/adamantem/releases/${releaseId}/copies`, body).then((r) => r.data);
export const adamantemCopyDelete = (variantId) =>
  localApi.delete(`/adamantem/copies/${variantId}`).then((r) => r.data);
export const reviewsList = (status = "") =>
  localApi.get(`/adamantem/reviews`, { params: status ? { status } : {} }).then((r) => r.data);
export const reviewsSetMode = (mode) => localApi.put(`/adamantem/reviews/settings`, { mode }).then((r) => r.data);
export const reviewsRerun = (id) => localApi.post(`/adamantem/reviews/${id}/rerun`).then((r) => r.data);
export const reviewsRemind = (id) => localApi.post(`/adamantem/reviews/${id}/remind`).then((r) => r.data);
export const reviewsOverride = (id) => localApi.post(`/adamantem/reviews/${id}/override`).then((r) => r.data);
export const reviewsDismiss = (id) => localApi.post(`/adamantem/reviews/${id}/dismiss`).then((r) => r.data);
export const reviewsSweep = (releaseIds) => localApi.post("/adamantem/reviews/sweep", { release_ids: releaseIds }).then((r) => r.data);
export const adamantemSyncSona = () =>
  localApi.post(`/adamantem/sync-sona`).then((r) => r.data);

// ---- Solicitudes de retiro (artista -> admin) y archivo de la bandeja ----
export const requestTakedown = (payload) =>
  localApi.post(`/release-requests`, payload).then((r) => r.data);
export const getReleaseRequest = (releaseId) =>
  localApi.get(`/release-requests/release/${releaseId}`).then((r) => r.data?.request || null);
export const listReleaseRequests = (state = "pending") =>
  localApi.get(`/release-requests`, { params: { state } }).then((r) => r.data?.requests || []);
export const decideReleaseRequest = (reqId, payload) =>
  localApi.post(`/release-requests/${reqId}/decide`, payload).then((r) => r.data);
export const markAdamantemTakedownDone = (reqId) =>
  localApi.post(`/release-requests/${reqId}/adamantem-done`).then((r) => r.data);
export const listArchivedReleases = () =>
  localApi.get(`/release-requests/archive`).then((r) => r.data?.archived || []);
export const setReleaseArchived = (releaseId, archived, note = "") =>
  localApi.post(`/release-requests/archive/${releaseId}`, { archived, note }).then((r) => r.data);
export const getMyDistribution = (releaseId) =>
  localApi.get(`/adamantem/my-release/${releaseId}`).then((r) => r.data);
// Productor IA (gratis, solo para distribuir): MP3 -> master WAV
export const startMaster = (body) => localApi.post(`/productor/master`, body).then((r) => r.data);
export const getMasterJob = (jobId) => localApi.get(`/productor/jobs/${jobId}`).then((r) => r.data);
export const deleteMyRelease = (releaseId) =>
  localApi.delete(`/adamantem/my-release/${releaseId}`).then((r) => r.data);
export const getReleaseForEdit = (releaseId) =>
  localApi.get(`/adamantem/my-release/${releaseId}/edit`).then((r) => r.data);
export const saveReleaseEdit = (releaseId, body) =>
  localApi.post(`/adamantem/my-release/${releaseId}/edit`, body).then((r) => r.data);
export const fixMyRelease = (releaseId, body) =>
  localApi.post(`/adamantem/my-release/${releaseId}/fix`, body).then((r) => r.data);

// ---- Subcuentas de manager / sello ----
export const managersMe = () => localApi.get(`/managers/me`).then((r) => r.data);
export const managersMyReleases = () => localApi.get(`/managers/me/releases`).then((r) => r.data);
export const managersAddArtist = (payload) => localApi.post(`/managers/me/artists`, payload).then((r) => r.data);
export const managersRemoveArtist = (id) => localApi.delete(`/managers/me/artists/${id}`).then((r) => r.data);
export const managersGrantArtist = (id, email, perms = { accounting: true, insights: true }) => localApi.post(`/managers/me/artists/${id}/access`, { email, ...perms }).then((r) => r.data);
export const managersRevokeArtist = (id) => localApi.delete(`/managers/me/artists/${id}/access`).then((r) => r.data);
export const managersAddMember = (payload) => localApi.post(`/managers/me/team`, payload).then((r) => r.data);
export const managersRemoveMember = (id) => localApi.delete(`/managers/me/team/${id}`).then((r) => r.data);
export const managersUpdateMember = (id, perms) => localApi.patch(`/managers/me/team/${id}`, { perms }).then((r) => r.data);
// Proyectos por artista, lanzamientos guardados por perfil y borradores en el servidor
export const managersProject = (artistId) => localApi.get(`/managers/me/artists/${artistId}/project`).then((r) => r.data);
export const managersLinkRelease = (releaseId, payload) => localApi.post(`/managers/me/releases/${releaseId}/link`, payload).then((r) => r.data);
export const managersCreateDraft = (payload) => localApi.post(`/managers/me/drafts`, payload).then((r) => r.data);
export const managersGetDraft = (id) => localApi.get(`/managers/me/drafts/${id}`).then((r) => r.data);
export const managersSaveDraft = (id, payload) => localApi.put(`/managers/me/drafts/${id}`, payload).then((r) => r.data);
export const managersDeleteDraft = (id) => localApi.delete(`/managers/me/drafts/${id}`).then((r) => r.data);
export const adminLabelOverview = () => localApi.get(`/managers/overview`).then((r) => r.data);
// Finanzas del sello (subcuentas con la herramienta activada) y acuerdo de Always por artista
export const managersFinance = (desde, hasta) => localApi.get(`/managers/me/finance`, { params: { desde, hasta } }).then((r) => r.data);
export const managersExpenses = (params = {}) => localApi.get(`/managers/me/finance/expenses`, { params }).then((r) => r.data);
export const managersAddExpense = (payload) => localApi.post(`/managers/me/finance/expenses`, payload).then((r) => r.data);
export const managersDeleteExpense = (id) => localApi.delete(`/managers/me/finance/expenses/${id}`).then((r) => r.data);
export const adminSignedArtists = () => localApi.get(`/managers/signed`).then((r) => r.data?.artists || []);
export const adminFinanceGlobal = (desde, hasta, manager_id = "") => localApi.get(`/managers/finance-global`, { params: { desde, hasta, ...(manager_id ? { manager_id } : {}) } }).then((r) => r.data);
export const adminExpenses = (mid, params = {}) => localApi.get(`/managers/${mid}/expenses`, { params }).then((r) => r.data);
export const adminAddExpense = (mid, payload) => localApi.post(`/managers/${mid}/expenses`, payload).then((r) => r.data);
export const adminDeleteExpense = (mid, id) => localApi.delete(`/managers/${mid}/expenses/${id}`).then((r) => r.data);
export const adminSetArtistDeal = (managerId, artistId, label_pct) => localApi.patch(`/managers/${managerId}/artists/${artistId}/deal`, { label_pct }).then((r) => r.data);
export const adminListManagers = () => localApi.get(`/managers`).then((r) => r.data?.managers || []);
export const adminCreateManager = (payload) => localApi.post(`/managers`, payload).then((r) => r.data);
export const adminInviteManager = (id, password = "") => localApi.post(`/managers/${id}/invite`, { password }).then((r) => r.data);
export const adminUpdateManager = (id, payload) => localApi.patch(`/managers/${id}`, payload).then((r) => r.data);
export const adminManagerArtists = (id) => localApi.get(`/managers/${id}/artists`).then((r) => r.data?.artists || []);
export const adminSetArtistPct = (mid, aid, artist_pct) => localApi.patch(`/managers/${mid}/artists/${aid}`, { artist_pct }).then((r) => r.data);
export const getSplitInvite = (token) => localApi.get(`/managers/split/${token}`).then((r) => r.data);
export const signSplitInvite = (token, payload) => localApi.post(`/managers/split/${token}/sign`, payload).then((r) => r.data);
export const adminAssignRelease = (payload) => localApi.post(`/managers/assign`, payload).then((r) => r.data);
export const adminUnassignRelease = (releaseId) => localApi.delete(`/managers/assign/${releaseId}`).then((r) => r.data);
export const adminGetAssignment = (releaseId) => localApi.get(`/managers/assign/${releaseId}`).then((r) => r.data?.assignment || null);
// ---- API de salida de la distribuidora (regalías, métricas, solicitudes) ----
export const getReleaseRoyalties = (releaseId, period) =>
  localApi.get(`/adamantem/releases/${releaseId}/royalties`, { params: { period } }).then((r) => r.data);
export const getReleaseMetrics = (releaseId, period) =>
  localApi.get(`/adamantem/releases/${releaseId}/metrics`, { params: { period } }).then((r) => r.data);
export const refreshAdamantemRequest = (reqId) =>
  localApi.post(`/release-requests/${reqId}/adamantem-refresh`).then((r) => r.data);

// ---- Borradores de lanzamiento (los prepara el Mánager SONA, p. ej. por WhatsApp) ----
export const getReleaseDraft = (token) =>
  api.get(`/release-drafts/by-token/${encodeURIComponent(token)}`).then((r) => r.data);
export const markReleaseDraftUsed = (draftId, releaseId) =>
  api.post(`/release-drafts/${draftId}/used`, { release_id: releaseId }).then((r) => r.data);

// ---- Mánager SONA: gestiones y WhatsApp (mismo backend que la app) ----
export const managerTask = ({ user_id, task, brief }) =>
  api.post("/manager/task", { user_id, task, brief }).then((r) => r.data);
export const waStatus = (userId) => api.get(`/whatsapp/status/${userId}`).then((r) => r.data);
export const waBuy = (userId) => api.post("/whatsapp/addon/buy", { user_id: userId }).then((r) => r.data);
export const waLinkStart = (userId) => api.post("/whatsapp/link/start", { user_id: userId }).then((r) => r.data);
export const waUnlink = (userId) => api.post("/whatsapp/unlink", { user_id: userId }).then((r) => r.data);

// ---- Portadas con IA (en SONA, con la clave de IA de SONA) ----
export const coverAiQuota = (userId) => api.get(`/covers/ai/quota/${userId}`).then((r) => r.data).catch(() => null);
export const coverAi = (userId, file, mode) => {
  const fd = new FormData();
  fd.append("user_id", userId);
  fd.append("mode", mode);
  fd.append("file", file);
  return api.post("/covers/ai", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 }).then((r) => r.data);
};

// ---- Hoja de ruta (plan de lanzamientos: calendario, fases, presupuesto, ingresos y recoup) ----
export const roadmapMe = () => localApi.get("/roadmap/me").then((r) => r.data);
export const roadmapList = (params = {}) => localApi.get("/roadmap", { params }).then((r) => r.data);
export const roadmapCreate = (body) => localApi.post("/roadmap", body).then((r) => r.data);
export const roadmapGet = (id) => localApi.get(`/roadmap/${id}`).then((r) => r.data);
export const roadmapSave = (id, body) => localApi.put(`/roadmap/${id}`, body).then((r) => r.data);
export const roadmapArchive = (id) => localApi.delete(`/roadmap/${id}`).then((r) => r.data);
export const roadmapInsights = (id) => localApi.get(`/roadmap/${id}/insights`).then((r) => r.data);
export const roadmapHistory = (id) => localApi.get(`/roadmap/${id}/history`).then((r) => r.data);
export const roadmapLink = (id, songId, code) => localApi.post(`/roadmap/${id}/songs/${songId}/link`, { code }).then((r) => r.data);
export const roadmapUnlink = (id, songId) => localApi.delete(`/roadmap/${id}/songs/${songId}/link`).then((r) => r.data);
export const roadmapShare = (id, opts) => localApi.post(`/roadmap/${id}/share`, opts).then((r) => r.data);
export const roadmapUnshare = (id) => localApi.delete(`/roadmap/${id}/share`).then((r) => r.data);
export const roadmapPublic = (token) => localApi.get(`/roadmap/public/${token}`).then((r) => r.data);
export const roadmapAdminAccess = () => localApi.get("/roadmap/admin/access").then((r) => r.data);
export const roadmapAdminEnable = (body) => localApi.post("/roadmap/admin/access", body).then((r) => r.data);
export const roadmapAdminDisable = (id) => localApi.delete(`/roadmap/admin/access/${id}`).then((r) => r.data);
